import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { isEqual } from "lodash";


const isNotEqual = (stateValue, propValue) => {
	if(!stateValue || !propValue || JSON.stringify(stateValue) === JSON.stringify(propValue)) {
		return false;
	}

	let notEqual = false;
	stateValue.forEach((val, index) => {
		if(!notEqual) {
			const rt = propValue[index] ?? {};
			if(val.member !== rt.member || val.role !== rt.role) {
				notEqual = true;
			} else if (val.associations?.length !== rt.associations?.length) {
				const last = val.associations[val.associations.length - 1];
				notEqual = last?.association || last?.credit;
			} else if (JSON.stringify(val.associations) !== JSON.stringify(rt.associations)) {
				notEqual = true;
			}
		}
	});

	return notEqual;
};

const convertAssociations = associations => {
	return (associations || []).map(association => ({
		id: association.id,
		text: association.name,
		isCalendarAppointment: association.isCalendarAppointment,
		isAcademicAppointment: association.isAcademicAppointment,
		isSummerAppointment: association.isSummerAppointment,
		academicMonths: association.academicMonths,
		calendarMonths: association.calendarMonths,
		summerMonths: association.summerMonths,
		disabled: association.active === false,
		associationType: "internal"
	}));
};

const convertExternalAssociations = associations => {
	return (associations || []).map(association => ({
		id: association.organizationId,
		text: association.organizationName,
		disabled: association.active === false,
		associationType: "external",
	}));
};

const createNewAssociationIfNeededAndRemoveEmpty = (initialAssociation, associations) => {
	if (associations.length === 0) {
		associations.push({
			...initialAssociation,
			costShareEffort: {
				...initialAssociation.costShareEffort,
			},
			sponsoredEffort: {
				...initialAssociation.sponsoredEffort,
			},
		});
	}
	// Adds in missing effort groups
	// Remove when this data is coming from the backend
	associations = associations.map(association => ({
		...initialAssociation,
		costShareEffort: {
			...initialAssociation.costShareEffort,
		},
		sponsoredEffort: {
			...initialAssociation.sponsoredEffort,
		},
		...association,
	})).filter(Boolean);

	return associations;
};

const createNewExtAssociationIfNeededAndRemoveEmpty = externalAssociations => {
	if (externalAssociations.length === 0) {
		externalAssociations.push({
			association: null
		});
	}
	// Adds in missing effort groups
	// Remove when this data is coming from the backend
	externalAssociations = externalAssociations.map(association => ({
		association: association.association,
	})).filter(Boolean);

	return externalAssociations;
};

const cleanupForPayload = content => {
	let members = [];

	for (const originalMember of content) {
		const associationsKey = originalMember.associationType === "external" ? "externalAssociations" : "associations";
		const member = {
			associationType: originalMember.associationType,
			member: originalMember.member,
			role: originalMember.role,
			associations: [],
			externalAssociations: [],
			isInitialCalculationRequired: originalMember.isInitialCalculationRequired,
			guest: originalMember.guest
		};

		for (const association of originalMember[associationsKey]) {
			// save association event the deep values of this object is empty
			// otherwise the logic of data comparison is violated because associations can be empty or [ initialAssociation ]
			// this can be fixed after refactoring this component
			member[associationsKey].push(association);
		}
		members.push(member);
	}

	return members;
};

const calculateCompleteCredit = value => {
	if (!isPresent(value)) {
		return 0;
	}

	return value.reduce((total, current) => {
		if (!isPresent(current.associations)) {
			return 0;
		}

		return current.associations.reduce((memberTotal, association) => {
			return memberTotal + (isPresent(association?.credit) ? association.credit : 0);
		}, 0) + total;
	}, 0);
};

const hasEmptyAssociation = associations => {
	return associations.find(association => isEmptyAssociation(association)) !== undefined;
};

const isEmptyAssociation = association => {
	return association.association === undefined || association.association === null;
};

const hasPrincipalInvestigatorFn = teamMembers => {
	return teamMembers.find(teamMember => isPrincipalInvestigator(teamMember)) !== undefined;
};

const isPrincipalInvestigator = teamMember => {
	return teamMember.role === "principal-investigator";
};

const validateEffort = (value, maxValue, isFieldRequired, errors = []) => {
	validateMinMax(value, maxValue, errors);
	validateFractionSize(value, errors);
	validateIsRequired(value, isFieldRequired, errors);

	return errors;
};

const validateIsRequired = (value, isFieldRequired = true, errors = []) => {
	if (!isPresent(value) && isFieldRequired) {
		errors.push("isRequired");
	}

	return errors;
};

const validateCredit = (value, isFieldRequired, errors = []) => {
	validateIsRequired(value, isFieldRequired, errors);
	validateMinMax(value, 100, errors);

	return errors;
};
const validateMinMax = (value, maxValue, errors = []) => {
	if (value !== undefined && value !== null && value < 0) {
		errors.push("minValue");
	}
	if (value !== undefined && value !== null && value > maxValue) {
		errors.push("maxValue");
	}

	return errors;
};

const validateFractionSize = (value, errors = []) => {
	if (Number.parseFloat((value * 100).toFixed(2)).toString().includes(".")) {
		errors.push("invalidFraction");
	}

	return errors;
};

const isPresent = value => {
	return value !== undefined && value !== null && value !== "";
};

const deepCopy = value => {
	return JSON.parse(JSON.stringify(value));
};

const sanitizeNumberValue = value => {
	if(value === ".") {
		return null;
	}

	if (!isNonEmptyString(value)) {
		return value ?? null;
	}

	return value.replace("e", "")
		.replace("E", "")
		.replace("-", "");
};

const researchTeamPayloadChanged = (values, cahngedValues) => {
	if(values.length !== cahngedValues?.length) {
		return true;
	}

	let isChnaged = false;

	for (let i = 0; i < values.length; i++) {
		if (isChnaged) {
			break;
		}

		isChnaged = values[i].member !== cahngedValues[i].member ||
			values[i].guest !== cahngedValues[i].guest ||
			values[i].role !== cahngedValues[i].role ||
			values[i].associationType !== cahngedValues[i].associationType ||
			values[i].associations.length !== cahngedValues[i].associations.length;

		if (isChnaged) {
			break;
		}

		for (let j = 0; j < values[i].associations.length; j++) {
			isChnaged = values[i].associations[j].association !== cahngedValues[i].associations[j].association;
		}

		if (isChnaged) {
			break;
		}

		isChnaged = !isEqual(values[i].externalAssociations, cahngedValues[i].externalAssociations);
	}

	return isChnaged;
};

export {
	isNotEqual,
	convertAssociations,
	createNewAssociationIfNeededAndRemoveEmpty,
	createNewExtAssociationIfNeededAndRemoveEmpty,
	cleanupForPayload,
	calculateCompleteCredit,
	hasEmptyAssociation,
	isEmptyAssociation,
	hasPrincipalInvestigatorFn,
	isPrincipalInvestigator,
	validateEffort,
	validateIsRequired,
	validateMinMax,
	validateFractionSize,
	isPresent,
	deepCopy,
	convertExternalAssociations,
	sanitizeNumberValue,
	researchTeamPayloadChanged,
	validateCredit,
};